import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import AddOnPhoto from "ui/AddOnPhoto"
import { numberToCurrency } from "helpers/string"
import { stepAddOnQuantity } from "components/bookings/helpers"
import styles from "./WidgetCart.module.css"

// Collapsible add-on list under a cart item. Quantities follow each add-on's per-order limits.
const CartAddOns = ({ addOns, selections, onChange, isInitiallyOpen }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(isInitiallyOpen)
  const available = (addOns || []).filter((addOn) => addOn && addOn.enabled !== false)
  if (!available.length) return null

  const selectedCount = available.filter((addOn) => Number(selections?.[addOn.id] || 0) > 0).length

  const setQuantity = (addOn, delta) => {
    const next = { ...selections }
    const quantity = stepAddOnQuantity(addOn, selections?.[addOn.id], delta)
    if (quantity <= 0) delete next[addOn.id]
    else next[addOn.id] = quantity
    onChange(next)
  }

  return (
    <Flex vertical gap={12}>
      <button
        type="button"
        className={styles.addOnsToggle}
        aria-expanded={isOpen}
        aria-label={t(isOpen ? "booking_widget.cart.hide_add_ons" : "booking_widget.cart.show_add_ons")}
        onClick={() => setIsOpen((value) => !value)}
      >
        <span>
          <span className={styles.addOnsTitle}>{t("booking_widget.cart.add_ons")}</span>
          {!isOpen && selectedCount > 0 && <span className={styles.addOnsCount}> · {t("booking_widget.cart.add_ons_selected", { count: selectedCount })}</span>}
        </span>
        <Icon type="feather" name={isOpen ? "ChevronUp" : "ChevronDown"} width={16} height={16} color="currentColor" />
      </button>
      {isOpen && (
        <Flex vertical gap={8}>
          {available.map((addOn) => {
            const quantity = Number(selections?.[addOn.id] || 0)
            const max = addOn.max_per_order ?? Infinity
            return (
              // Photo, text, price, counter in a row; on a phone the price and counter go under the text.
              <div key={addOn.id} className={styles.addOnRow}>
                <AddOnPhoto src={addOn.photo_url} alt={addOn.title} size={52} iconSize={20} className={styles.addOnPhoto} />
                <Flex vertical className={styles.addOnText}>
                  <span className={styles.addOnName}>{addOn.title}</span>
                  {addOn.description && <span className={styles.addOnDescription}>{addOn.description}</span>}
                </Flex>
                <span className={styles.addOnPrice}>{numberToCurrency(addOn.price)}</span>
                <Flex align="center" gap={8} className={styles.addOnCounter}>
                  <Button
                    shape="circle"
                    className={styles.counterButton}
                    aria-label={t("booking_widget.cart.fewer", { name: addOn.title })}
                    disabled={quantity === 0}
                    onClick={() => setQuantity(addOn, -1)}
                    icon={<Icon type="feather" name="Minus" width={16} height={16} color="currentColor" />}
                  />
                  <span className={styles.counterValue}>{quantity}</span>
                  <Button
                    shape="circle"
                    className={styles.counterButton}
                    aria-label={t("booking_widget.cart.more", { name: addOn.title })}
                    disabled={quantity >= max}
                    onClick={() => setQuantity(addOn, 1)}
                    icon={<Icon type="feather" name="Plus" width={16} height={16} color="currentColor" />}
                  />
                </Flex>
              </div>
            )
          })}
        </Flex>
      )}
    </Flex>
  )
}

CartAddOns.propTypes = {
  addOns: PropTypes.array,
  selections: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  isInitiallyOpen: PropTypes.bool
}

CartAddOns.defaultProps = {
  addOns: [],
  selections: {},
  isInitiallyOpen: true
}

export default CartAddOns
